import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraCommandPalette, SpecraIconButton, SpecraLogo, SpecraSearchTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Moon, Sparkles, Sun } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, matchPath, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { appPageMetadata } from '../apps/registry';
import { AssistRail } from '../components/AssistRail';
import { BackButton } from '../components/BackButton';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { BugReportButton } from '../components/BugReport';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { ProjectSelector } from '../components/ProjectSelector';
import { ProposalProgressBar } from '../components/ProposalDashboard/ProposalProgressBar';
import { ProjectActionsMenu } from '../components/ProposalDashboard/v2/ProjectActionsMenu';
import { ProcessingJobsHost } from '../components/processing/ProcessingJobsHost';
import { ProcessingTray } from '../components/processing/ProcessingTray';
import { useProposalRecordShellEnabled } from '../components/RecordShell/recordShellFlag';
import { useSharedRecordCommandItems } from '../components/SharedRecord';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/authContext';
import { useDarkMode } from '../contexts/darkModeContext';
import { ProposalProgressProvider } from '../contexts/ProposalProgressContext';
import { GuideHost } from '../guides/GuideHost';
import { useWorkflowAreas } from '../lib/access/useWorkflowAreas';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { useCommandPaletteItems } from './useCommandPaletteItems';
// Roles that see the Admin console entry (mirrors AppSidebar / RequireAdminRoute).
const ADMIN_ROLES = new Set([
    'admin',
    'internal'
]);
// Define page metadata with pattern support
const pageMetadata = [
    {
        path: '/growth/home',
        title: 'Growth Home',
        description: 'Your authorized company portfolio and active work'
    },
    // Growth redesign (GRW-01): these pages hide the body header themselves
    // (setShowHeader(false)); the titles keep the fallback from reading "Dashboard".
    {
        path: '/growth/rfi-responses',
        title: 'RFI responses',
        description: 'RFI response workspaces'
    },
    {
        path: '/growth/proposals',
        title: 'Proposals',
        description: 'Proposal workspaces'
    },
    {
        path: '/capture',
        title: 'Pursuits',
        description: 'Deal threads for the bids you are chasing'
    },
    {
        path: '/landscapes/:id/seats',
        title: 'Landscape seats',
        description: 'Who holds each seat in this landscape'
    },
    {
        path: '/',
        title: 'Dashboard',
        description: 'AI-Powered Government Contracting Assistant'
    },
    {
        path: '/assistant',
        title: 'Assistant',
        description: 'Ask complex questions to your Federal GovCon AI partner'
    },
    {
        path: '/assistant/:id',
        title: 'Assistant',
        description: 'Converse with the AI'
    },
    {
        path: '/documents',
        title: 'Documents',
        description: 'Manage and organize your uploaded documents'
    },
    {
        path: '/sharepoint-documents',
        title: 'SharePoint Documents',
        description: 'Access SharePoint documents with automatic ACL filtering'
    },
    {
        path: '/delivery',
        title: 'Delivery',
        description: 'Programs, audits, and the rest of the delivery domain'
    },
    {
        path: '/delivery/org',
        title: 'Org Hierarchy',
        description: 'Sectors, business units, and portfolios'
    },
    {
        path: '/registries',
        title: 'Registries',
        description: 'Companies, contacts, government orgs, vehicles, and labor categories'
    },
    {
        path: '/delivery/programs',
        title: 'Programs',
        description: 'Active program records and everything tied to them'
    },
    {
        path: '/delivery/programs/:id',
        title: 'Program detail',
        description: 'Single program — overview, audits, risks, financial, etc.'
    },
    {
        path: '/delivery/audit-templates',
        title: 'Audit Templates',
        description: 'Reusable audit definitions and their question sets'
    },
    {
        path: '/history',
        title: 'History',
        description: 'Review your past queries and generated artifacts'
    },
    {
        path: '/library',
        title: 'Library',
        description: 'Access your collection of templates and boilerplate'
    },
    {
        path: '/vault',
        title: 'Vault',
        description: 'Securely store and manage your proposal documents'
    },
    {
        path: '/settings',
        title: 'Settings',
        description: 'Manage your account settings'
    },
    {
        path: '/help',
        title: 'Help',
        description: 'Find answers and get support'
    }
];
export default function DashboardLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { projectId, id } = useParams();
    const { user } = useAuth();
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const pageHeaderContent = useLayoutStore((state)=>state.pageHeaderContent);
    const showHeader = useLayoutStore((state)=>state.showHeader);
    const breadcrumbItems = useLayoutStore((state)=>state.breadcrumbItems);
    const assistOpen = useLayoutStore((state)=>state.assistOpen);
    const setAssistOpen = useLayoutStore((state)=>state.setAssistOpen);
    const setAssistOrigin = useLayoutStore((state)=>state.setAssistOrigin);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [paletteQuery, setPaletteQuery] = useState('');
    const shared = useSharedRecordCommandItems(paletteQuery);
    // Open the app-wide Assist rail, remembering the route it was opened from so
    // "Open in full view" hands off to the right full-page assistant. Toggling
    // closed leaves the origin as-is (it's only read while open).
    const toggleAssist = useCallback(()=>{
        if (!assistOpen) setAssistOrigin(`${location.pathname}${location.search}`);
        setAssistOpen(!assistOpen);
    }, [
        assistOpen,
        location.pathname,
        location.search,
        setAssistOpen,
        setAssistOrigin
    ]);
    // ⌘J / Ctrl+J toggles Assist app-wide (LAYOUT-SPEC §7).
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (event.key === 'j' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggleAssist();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        toggleAssist
    ]);
    // Project mode: /project/:projectId/* always, and /proposal/:id/* while
    // growth_record_shell_v1 is off, keep the legacy slim strip (logo + project selector)
    // in place of the topbar. With the shell on, proposals render in the standard topbar
    // beside a record sidebar (FND-20). Proposal v2 is unconditional (OD-60, FND-23).
    const proposalShell = useProposalRecordShellEnabled();
    const isProjectRoute = location.pathname.startsWith('/project/') && projectId;
    const isProposalRoute = location.pathname.startsWith('/proposal/') && id;
    const isProjectMode = isProjectRoute || isProposalRoute;
    const showProjectStrip = !!isProjectRoute || !!isProposalRoute && !proposalShell;
    const assistHost = useLayoutStore((state)=>state.assistHost);
    const isAdmin = !!user?.role && ADMIN_ROLES.has(user.role.toLowerCase());
    // The palette offers the Growth workflows exactly where the rail does —
    // never while access is still resolving.
    const { isLoading: areasLoading, reachable, growthOnly } = useWorkflowAreas();
    const homeHref = growthOnly ? '/growth/home' : '/dashboard';
    const homeLabel = growthOnly ? 'Growth Home' : 'Dashboard';
    // Use matchPath to find matching metadata pattern
    const metadata = useMemo(()=>{
        // App packages first: they own their paths, and their patterns are more
        // specific than anything the shell's own table declares.
        for (const pattern of [
            ...appPageMetadata,
            ...pageMetadata
        ]){
            if (matchPath(pattern.path, location.pathname)) {
                return {
                    ...pattern,
                    description: pattern.description ?? ''
                };
            }
        }
        return {
            title: homeLabel,
            description: ''
        };
    }, [
        location.pathname,
        homeLabel
    ]);
    // Inside a program (the retired 'v2' route only redirects) the palette lists
    // that program's sections too.
    const programMatch = matchPath('/delivery/programs/:id/*', location.pathname);
    const programId = programMatch && programMatch.params['*'] !== 'v2' ? programMatch.params.id ?? '' : '';
    // Command palette destinations — every app section, grouped by workspace.
    const commandItems = useCommandPaletteItems({
        navigate,
        isAdmin,
        areasLoading,
        reachable,
        growthOnly,
        programId
    });
    const bodyHeader = showHeader && !isProjectMode ? /*#__PURE__*/ _jsx("div", {
        className: "border-b border-border bg-background px-8 pb-4 pt-5",
        children: pageHeaderContent ? pageHeaderContent : /*#__PURE__*/ _jsxs("div", {
            className: "min-w-0",
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    className: "truncate text-xl font-semibold leading-tight text-foreground",
                    children: metadata.title
                }),
                metadata.description && /*#__PURE__*/ _jsx("p", {
                    className: "truncate text-sm leading-tight text-muted-foreground",
                    children: metadata.description
                })
            ]
        })
    }) : null;
    return /*#__PURE__*/ _jsx(ProposalProgressProvider, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "fixed inset-0 flex flex-col overflow-hidden bg-background",
            children: [
                showProjectStrip && /*#__PURE__*/ _jsxs("div", {
                    className: "flex h-12 flex-shrink-0 items-center gap-3 px-4",
                    children: [
                        /*#__PURE__*/ _jsx(BackButton, {
                            homeHref: homeHref
                        }),
                        /*#__PURE__*/ _jsx(Link, {
                            className: "flex-shrink-0 transition-opacity hover:opacity-80",
                            to: homeHref,
                            children: /*#__PURE__*/ _jsx(SpecraLogo, {
                                className: "text-foreground",
                                size: 18,
                                variant: "mark"
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "h-4 w-px bg-border"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "min-w-0 max-w-[min(60vw,40rem)]",
                            children: /*#__PURE__*/ _jsx(ProjectSelector, {
                                currentProjectId: projectId || id,
                                isCollapsed: false
                            })
                        }),
                        isProposalRoute && /*#__PURE__*/ _jsx(ProjectActionsMenu, {}),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1"
                        }),
                        /*#__PURE__*/ _jsx(BugReportButton, {}),
                        /*#__PURE__*/ _jsx(NotificationBell, {})
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex min-h-0 flex-1",
                    children: [
                        /*#__PURE__*/ _jsx(Sidebar, {}),
                        /*#__PURE__*/ _jsxs("main", {
                            className: "flex min-w-0 flex-1 flex-col overflow-hidden rounded-tl-xl bg-card",
                            children: [
                                !showProjectStrip && /*#__PURE__*/ _jsxs("header", {
                                    className: "flex h-[var(--component-topbar-height)] flex-shrink-0 items-center gap-4 border-b border-border bg-background px-6",
                                    children: [
                                        /*#__PURE__*/ _jsx(BackButton, {
                                            homeHref: homeHref
                                        }),
                                        /*#__PURE__*/ _jsx(Breadcrumbs, {
                                            className: "min-w-0 flex-1",
                                            items: location.pathname === '/growth/home' ? [
                                                {
                                                    label: 'Growth Home',
                                                    href: '/growth/home'
                                                }
                                            ] : breadcrumbItems ?? undefined
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraSearchTrigger, {
                                            className: "hidden sm:flex",
                                            onClick: ()=>setPaletteOpen(true)
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            "aria-pressed": assistOpen,
                                            className: "hidden rounded-[var(--radius-pill)] sm:inline-flex",
                                            "data-testid": Testids.appNav.assistToggle,
                                            iconLeft: /*#__PURE__*/ _jsx(Sparkles, {
                                                "aria-hidden": "true"
                                            }),
                                            onClick: toggleAssist,
                                            size: "sm",
                                            title: "Assist (⌘J)",
                                            variant: assistOpen ? 'secondary' : 'outline',
                                            children: "Assist"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraIconButton, {
                                            "data-testid": Testids.appNav.themeToggle,
                                            label: isDarkMode ? 'Switch to light mode' : 'Switch to dark mode',
                                            onClick: toggleDarkMode,
                                            variant: "ghost",
                                            children: isDarkMode ? /*#__PURE__*/ _jsx(Sun, {
                                                "aria-hidden": "true"
                                            }) : /*#__PURE__*/ _jsx(Moon, {
                                                "aria-hidden": "true"
                                            })
                                        }),
                                        isProposalRoute && proposalShell ? /*#__PURE__*/ _jsx(ProjectActionsMenu, {}) : null,
                                        /*#__PURE__*/ _jsx(BugReportButton, {}),
                                        /*#__PURE__*/ _jsx(NotificationBell, {})
                                    ]
                                }),
                                bodyHeader,
                                /*#__PURE__*/ _jsx(ProposalProgressBar, {}),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex min-h-0 flex-1 flex-col overflow-auto",
                                    children: /*#__PURE__*/ _jsx(Outlet, {})
                                })
                            ]
                        }),
                        assistOpen && assistHost === 'rail' ? /*#__PURE__*/ _jsx(AssistRail, {
                            onClose: ()=>setAssistOpen(false)
                        }) : null
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraCommandPalette, {
                    items: paletteQuery.trim() ? [
                        ...commandItems,
                        ...shared.items
                    ] : commandItems,
                    loading: shared.loading,
                    query: paletteQuery,
                    onQueryChange: setPaletteQuery,
                    footer: shared.unavailable.length > 0 ? /*#__PURE__*/ _jsxs("span", {
                        "data-testid": Testids.sharedRecord.searchPartial,
                        children: [
                            "Some results couldn't be searched (",
                            shared.unavailable.join(', '),
                            "). Try again or browse Registries and Library."
                        ]
                    }) : undefined,
                    onOpenChange: setPaletteOpen,
                    open: paletteOpen,
                    placeholder: "Search sections, registries and records…"
                }),
                /*#__PURE__*/ _jsx(ProcessingJobsHost, {}),
                /*#__PURE__*/ _jsx(ProcessingTray, {}),
                /*#__PURE__*/ _jsx(GuideHost, {})
            ]
        })
    });
}
